import {Separator} from "@/components/ui/separator";
import {cn} from "@/lib/utils";

import type {PermanentPermitsKpi} from "../types";
import {DeltaPill} from "./delta-pill";
import {KpiCard, type KpiCardDensity} from "./kpi-card";

type KpiCardPermitsProps = {
  kpi: PermanentPermitsKpi;
  icon?: React.ElementType;
  density?: KpiCardDensity;
  selected?: boolean;
  onSelect?: () => void;
};

/**
 * Dual-metric tile for permanent permits.
 *
 * - `current_active`: snapshot count of permits valid right now (no period).
 * - `issued_in_period`: how many were created during the selected window,
 *   with delta vs. the previous window.
 */
export function KpiCardPermits({
  kpi,
  icon,
  density = "default",
  selected,
  onSelect,
}: KpiCardPermitsProps) {
  const isCompact = density === "compact";
  const n = isCompact ? "text-3xl" : "text-4xl";
  const subLabel = "text-muted-foreground text-xs font-semibold uppercase tracking-wide";

  return (
    <KpiCard
      label={kpi.label ?? "Permanent permits"}
      icon={icon}
      density={density}
      selected={selected}
      onSelect={onSelect}
    >
      <div className="flex flex-col gap-1">
        <span className={subLabel}>Active now</span>
        <span className={cn("text-foreground font-bold tracking-tight tabular-nums", n)}>
          {kpi.current_active.toLocaleString()}
        </span>
      </div>
      <div className={cn("mt-auto flex flex-col gap-1", !isCompact && "mt-2")}>
        <Separator className={isCompact ? "my-0.5" : undefined} />
        <div className="flex items-center justify-between gap-3">
          <span className={subLabel}>Issued in period</span>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1">
            <span className="text-foreground min-w-0 text-lg font-bold tabular-nums">
              {kpi.issued_in_period.toLocaleString()}
            </span>
            <DeltaPill
              value={kpi.delta_pct_issued}
              size={isCompact ? "sm" : "default"}
              className="shrink-0"
            />
          </div>
        </div>
      </div>
    </KpiCard>
  );
}
